import React, { useState, useEffect, useCallback } from 'react';
import {
  DocumentArrowDownIcon,
  PaperClipIcon,
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  UserCircleIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  DocumentMagnifyingGlassIcon,
  DocumentPlusIcon,
  ExclamationCircleIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import { showSuccess, showError } from '../../utils/notificationHelper';
import { disputesAPI } from '../../services/api';
import ConfirmationModal from '../common/ConfirmationModal';
import FileViewerModal from '../common/FileViewerModal';

const EvidenceTabContent = ({ dispute, onRefresh }) => {
  // State for evidence files
  const [evidenceFiles, setEvidenceFiles] = useState(dispute?.evidence_files || []);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false); // Track loading state for parent content
  const [showAddEvidenceModal, setShowAddEvidenceModal] = useState(false);
  const [evidenceFileSelected, setEvidenceFileSelected] = useState(null);
  const [errors, setErrors] = useState({});

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [evidenceToDelete, setEvidenceToDelete] = useState(null);

  // Evidence viewer modal state
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [evidenceToView, setEvidenceToView] = useState(null);

  // Form data for adding evidence
  const [evidenceFormData, setEvidenceFormData] = useState({
    title:            '',
    description:      '',
    submitted_by:     'complainant',
  });

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';

    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year:   'numeric',
      month:  'long',
      day:    'numeric'
    }).format(date);
  };

  // Format time of day
  const formatTime = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      hour:   'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  // Load evidence files when the component mounts
  useEffect(() => {
    if (dispute?.id) {
      fetchEvidenceFiles();
    }
  }, [dispute?.id]);

  // Fetch evidence files from API
  const fetchEvidenceFiles = useCallback(async () => {
    if (!dispute?.id) return;

    try {
      setEvidenceLoading(true);
      const response = await disputesAPI.getEvidenceFiles(dispute.id);
      setEvidenceFiles(response.data || []);
    } catch (error) {
      console.error('Error fetching evidence files:', error);
      showError('Failed to load evidence files');
    } finally {
      setEvidenceLoading(false);
    }
  }, [dispute?.id]);

  // Handle form field changes
  const handleEvidenceFormChange = (e) => {
    const { name, value } = e.target;
    setEvidenceFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file selection
  const handleEvidenceFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setEvidenceFileSelected(e.target.files[0]);
    }
  };

  // Reset form
  const resetEvidenceForm = () => {
    setEvidenceFormData({
      title:        '',
      description:  '',
      submitted_by: 'complainant',
    });
    setEvidenceFileSelected(null);
    setErrors({});
    setShowAddEvidenceModal(false);
  };

  // Submit evidence
  const handleEvidenceSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const newErrors = {};
    if (!evidenceFormData.title.trim()) newErrors.title = 'Title is required';
    if (!evidenceFileSelected) newErrors.evidence_file = 'Evidence file is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('title', evidenceFormData.title);
      submitData.append('description', evidenceFormData.description || '');
      submitData.append('submitted_by', evidenceFormData.submitted_by);

      if (evidenceFileSelected) {
        submitData.append('evidence_file', evidenceFileSelected);
      }

      await disputesAPI.uploadEvidence(dispute.id, submitData);

      showSuccess('Evidence uploaded successfully');
      resetEvidenceForm();

      // Refresh data
      fetchEvidenceFiles();
      if (onRefresh) onRefresh();

    } catch (error) {
      console.error('Error uploading evidence:', error);
      showError('Failed to upload evidence');
    }
  };

  // View evidence file in modal
  const handleViewEvidence = (evidenceId) => {
    const evidence = evidenceFiles.find(ev => ev.id === evidenceId);
    setEvidenceToView(evidence);
    setShowViewerModal(true);
  };

  // Close viewer modal
  const closeViewerModal = () => {
    setEvidenceToView(null);
    setShowViewerModal(false);
  };

  // Download evidence file
  const handleDownloadEvidence = async (evidenceId) => {
    try {
      const response = await disputesAPI.downloadEvidence(dispute.id, evidenceId);

      // Create a blob from the response data
      const blob = new Blob([response.data]);

      // Create a link element and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get file info from evidence files array
      const evidence = evidenceFiles.find(ev => ev.id === evidenceId);
      const fileName = evidence?.filename || evidence?.file_name || `evidence-${evidenceId}.pdf`;

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      window.URL.revokeObjectURL(url);

      showSuccess('Evidence file downloaded');
    } catch (error) {
      console.error('Error downloading evidence:', error);
      showError('Failed to download evidence file');
    }
  };

  // Delete evidence file
  const handleDeleteEvidence = async (evidenceId) => {
    const evidence = evidenceFiles.find(ev => ev.id === evidenceId);

    // Set the evidence to delete and show confirmation modal
    setEvidenceToDelete(evidence);
    setShowConfirmModal(true);
  };

  // Confirm delete evidence
  const confirmDeleteEvidence = async () => {
    if (!evidenceToDelete) return;

    try {
      await disputesAPI.deleteEvidence(dispute.id, evidenceToDelete.id);

      showSuccess('Evidence deleted successfully');

      // Refresh data
      fetchEvidenceFiles();
      if (onRefresh) onRefresh();

    } catch (error) {
      console.error('Error deleting evidence:', error);
      showError('Failed to delete evidence');
    } finally {
      // Reset state
      setEvidenceToDelete(null);
      setShowConfirmModal(false);
    }
  };

  // Cancel delete evidence
  const cancelDeleteEvidence = () => {
    setEvidenceToDelete(null);
    setShowConfirmModal(false);
  };

  // Get file icon based on file type
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return <DocumentTextIcon className="h-5 w-5 text-red-500" />;
      case 'doc':
      case 'docx':
        return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <DocumentMagnifyingGlassIcon className="h-5 w-5 text-green-500" />;
      default:
        return <PaperClipIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';

    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Determine the party label based on the submitted_by value
  const getPartyLabel = (submittedBy) => {
    const parties = {
      'complainant': 'Complainant',
      'respondent': 'Respondent',
      'witness': 'Witness',
      'barangay_official': 'Barangay Official',
      'other': 'Other'
    };

    return parties[submittedBy] || 'Unknown';
  };

  return (
    <div className="">
        {contentLoading ? (
          <div className="flex justify-center items-center py-8 w-full">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500 mb-3"></div>
              <p className="text-sm text-gray-600"></p>
            </div>
          </div>
        ) : (
        <>
        <div className="flex items-center justify-between mb-6 pb-2 border-b border-gray-100">
          <div className="flex items-center">
            <div className="bg-teal-100 p-2 rounded-full mr-3">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-teal-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Submitted Evidence</h3>
          </div>

          {/* Only show add evidence button if case is not resolved */}
          {dispute.status !== 'settled' && dispute.status !== 'escalated' && (
            <button
              type="button"
              onClick={() => setShowAddEvidenceModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      transition-all duration-200 flex items-center"
            >
              <PaperClipIcon className="h-5 w-5 mr-2" />
              Add Evidence
            </button>
          )}
        </div>

        {evidenceLoading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
          </div>
        ) : (!evidenceFiles || evidenceFiles.length === 0) ? (
          <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg bg-gray-50">
            <ClipboardDocumentCheckIcon className="h-16 w-16 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 text-lg font-medium">No evidence files have been submitted</p>
            <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
              When evidence is submitted, images, documents, and other supporting materials will appear here
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {evidenceFiles.map((evidence, index) => (
              <div
                key={evidence.id}
                className="border border-gray-100 rounded-lg overflow-hidden hover:shadow-md
                          transition-all duration-300 bg-white "
              >
                {evidence.file_type && evidence.file_type.startsWith('image/') ? (
                  <div className="h-32 bg-gray-100 flex items-center justify-center relative group">
                    <img
                      src={evidence.file_url}
                      alt={evidence.filename}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-32 bg-gray-50 flex flex-col items-center justify-center">
                    <DocumentArrowDownIcon className="h-20 w-20 text-gray-500" />
                    <p className="text-sm text-gray-700 mt-2 bg-gray-100 px-3 py-1 rounded-full">
                      {(evidence.file_type || "").split('/')[1] || 'Document'}
                    </p>
                  </div>
                )}
                <div className="p-4 border-t border-teal-50">
                  <p className="text-sm font-medium text-gray-800 truncate mb-1">{evidence.title}</p>
                  <p className="text-xs text-gray-500 mb-2">{evidence.description || 'No description'}</p>
                  <div className="flex items-center text-xs text-gray-500 mb-3">
                    <UserCircleIcon className="h-4 w-4 mr-1" />
                    <span className="mr-3">By: {getPartyLabel(evidence.submitted_by)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => handleViewEvidence(evidence.id)}
                      className="flex-1 text-sm text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100
                                transition-colors duration-200 px-3 py-2 rounded-lg flex items-center justify-center"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View
                    </button>

                    {/* Only show delete button if case is not resolved */}
                    {dispute.status !== 'settled' && dispute.status !== 'escalated' && (
                      <button
                        type="button"
                        onClick={() => handleDeleteEvidence(evidence.id)}
                        className="text-sm text-red-700 hover:text-red-900 bg-red-50 hover:bg-red-100
                                  transition-colors duration-200 px-3 py-2 rounded-lg flex items-center justify-center"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}

      {/* Add Evidence Modal */}
      {showAddEvidenceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto">
          <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl animate-fade-in-down flex flex-col max-h-[calc(100vh-8px)] overflow-hidden">
              {/* Header with gradient background */}
              <div className="bg-gradient-to-r from-teal-50 to-white-100 px-6 py-4 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                      <ClipboardDocumentCheckIcon className="h-6 w-6 text-teal-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800">
                      Add Case Evidence
                    </h2>
                  </div>
                  <button
                    onClick={resetEvidenceForm}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-300
                            bg-white p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
                    aria-label="Close modal"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <small className="text-gray-600 mt-2 px-2">
                  Case No. <span className="font-medium text-gray-800">{dispute.case_number}</span> • &nbsp;
                  {dispute.complainant_name} vs. {dispute.respondent_name}
                </small>
              </div>

              <form onSubmit={handleEvidenceSubmit}>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">{/* Left column */}
                  <div className="space-y-5">
                    {/* Title */}
                    <div>
                      <label htmlFor="title" className="flex items-center text-gray-700 text-sm font-medium mb-1">
                        <span>Evidence Title</span>
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        id="title"
                        value={evidenceFormData.title}
                        onChange={handleEvidenceFormChange}
                        className={`w-full border ${
                          errors.title ? 'border-red-500' : 'border-gray-300'
                        } rounded-lg px-3 py-2 `}
                        placeholder="E.g., Witness Statement, Photo of Incident"
                        required
                      />
                      {errors.title && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                          <ExclamationCircleIcon className="h-4 w-4 mr-1" /> {errors.title}
                        </p>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label htmlFor="description" className="block text-gray-700 text-sm font-medium mb-1">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows={3}
                        value={evidenceFormData.description}
                        onChange={handleEvidenceFormChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 "
                        placeholder="Describe what this evidence shows or proves..."
                      />
                    </div>

                    {/* Submitted By */}
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Submitted By
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'complainant', label: 'Complainant', name: dispute.complainant_name },
                          { value: 'respondent', label: 'Respondent', name: dispute.respondent_name },
                          { value: 'barangay', label: 'Barangay', name: 'Official' }
                        ].map(option => (
                          <label
                            key={option.value}
                            className={`flex flex-col items-center justify-center p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                              evidenceFormData.submitted_by === option.value
                                ? 'bg-teal-50 border-teal-300 shadow-sm'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="submitted_by"
                              value={option.value}
                              checked={evidenceFormData.submitted_by === option.value}
                              onChange={handleEvidenceFormChange}
                              className="sr-only"
                            />
                            <UserCircleIcon className={`h-8 w-8 mb-1 ${
                              evidenceFormData.submitted_by === option.value
                                ? 'text-teal-500'
                                : 'text-gray-400'
                            }`} />
                            <span className={`text-sm font-medium ${
                              evidenceFormData.submitted_by === option.value
                                ? 'text-teal-700'
                                : 'text-gray-700'
                            }`}>
                              {option.label}
                            </span>
                            <span className="text-xs text-gray-500 truncate max-w-full">
                              {option.name || option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right column - File Upload */}
                  <div>
                    <div className="h-full flex flex-col">
                      <h3 className="text-gray-700 text-sm font-medium flex items-center mb-2">
                        Evidence File
                        <span className="text-red-500 ml-1">*</span>
                      </h3>

                      {/* File Upload Area */}
                      {!evidenceFileSelected ? (
                        <div className="flex-1 flex flex-col items-center justify-center">
                          <div className="mt-1 flex flex-col justify-center items-center px-6 pt-5 pb-6 border-2 border-gray-200 border-dashed rounded-lg bg-white w-full h-full">
                            <div className="space-y-3 text-center">
                              <DocumentTextIcon className="mx-auto h-16 w-16 text-teal-500" />
                              <div>
                                <label htmlFor="evidence-file-upload" className="relative cursor-pointer bg-teal-100 rounded-md font-medium text-teal-700 hover:text-teal-900 px-4 py-2 inline-flex items-center shadow-sm hover:bg-teal-200 transition-colors duration-200 focus-within:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                  <PaperClipIcon className="h-4 w-4 mr-2" />
                                  <span>Select File</span>
                                  <input
                                    id="evidence-file-upload"
                                    name="evidence_file"
                                    type="file"
                                    onChange={handleEvidenceFileChange}
                                    className="sr-only"
                                    required
                                  />
                                </label>
                              </div>
                              <p className="text-sm text-gray-500">
                                Or drag and drop file here
                              </p>
                              <p className="text-xs text-gray-500">
                                PDF, JPG, PNG, DOC up to 10MB
                              </p>
                            </div>
                          </div>
                          {errors.evidence_file && (
                            <p className="text-red-500 text-xs mt-2 self-start flex items-center">
                              <ExclamationCircleIcon className="h-4 w-4 mr-1" /> {errors.evidence_file}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex-1 flex flex-col">
                          <div className="mb-2 text-sm text-teal-700">Selected File:</div>

                          {/* File Preview Card */}
                          <div className="flex-1 bg-white rounded-lg border border-teal-200 p-4 relative">
                            <button
                              type="button"
                              onClick={() => setEvidenceFileSelected(null)}
                              className="absolute top-2 right-2 text-red-500 hover:text-red-700 bg-white rounded-full p-1 shadow-sm hover:bg-red-50 transition-colors duration-200"
                              aria-label="Remove file"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>

                            <div className="flex flex-col items-center justify-center h-full space-y-4">
                              <div className="bg-teal-50 p-4 rounded-full">
                                <DocumentTextIcon className="h-12 w-12 text-teal-500" />
                              </div>

                              <div className="text-center">
                                <p className="font-medium text-gray-900 break-all max-w-full">{evidenceFileSelected.name}</p>
                                <p className="text-sm text-gray-500">
                                  {(evidenceFileSelected.size / 1024).toFixed(2)} KB • {evidenceFileSelected.type || 'Unknown Type'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Form Controls */}
                <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetEvidenceForm}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all duration-200 shadow-sm flex items-center"
                  >
                    <PaperClipIcon className="h-5 w-5 mr-2" />
                    Submit Evidence
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}

      {/* Delete Evidence Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={cancelDeleteEvidence}
        onConfirm={confirmDeleteEvidence}
        title="Delete Evidence"
        message={`Are you sure you want to delete "${evidenceToDelete?.title || 'this evidence'}"? This action cannot be undone and will permanently remove the evidence file from the case.`}
        confirmText="Delete Evidence"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        icon="warning"
      />

      {/* Evidence Viewer Modal */}
      <FileViewerModal
        isOpen={showViewerModal}
        onClose={closeViewerModal}
        file={evidenceToView}
        onDownload={handleDownloadEvidence}
      />
    </div>
  );
};

export default React.memo(EvidenceTabContent);
